/**
 * Generic recurring-poll driver with exponential backoff and a pause/resume
 * switch for visibility — framework-agnostic (no React, no `document`) so it
 * can drive both a component's useEffect-based poll (UploadDetailPanel) and a
 * plain callback-based poll (UploadHistoryContext's pollUploadStatus, which
 * is a useCallback, not a hook, and can't use another hook internally).
 *
 * Why backoff matters here specifically: this app's local dev setup routes
 * through a free ngrok tunnel that starts dropping/resetting requests under
 * sustained load (see UploadDetailPanel.tsx's RECORDS_POLL_MS docstring). A
 * failed poll retried instantly at the same rate hits a struggling tunnel at
 * full volume precisely when it's least able to take it — backing off gives
 * it room to recover instead of compounding the problem.
 *
 * Why pause/resume rather than tearing the timer down and rebuilding it:
 * visibility flips (alt-tab, minimize) are frequent and transient — routing
 * them through the same effect-dependency machinery that already caused the
 * cache-churn bug (a stable value changing identity tears down and rebuilds
 * the whole poll effect, including its listeners) is exactly the failure
 * mode to avoid. pause()/resume() mutate this poller's own internal state
 * without touching the effect that created it.
 */

export interface BackoffPollerOptions {
  /** Delay before the first poll, and the delay used again after any
   *  successful poll — the "healthy" cadence. */
  intervalMs: number;
  /** Ceiling for the exponentially-growing delay after consecutive
   *  failures — without this a persistent outage would back off forever
   *  toward an unusably long delay. */
  maxIntervalMs: number;
  /** One poll attempt. Resolve normally for "the request reached the
   *  server and got an answer" — including an answer that isn't done yet
   *  (e.g. status still "processing"); that's not a failure, it's expected
   *  and should keep polling at the normal cadence. Reject only for "this
   *  attempt to reach the server itself failed" (network error, timeout,
   *  thrown exception) — that's what should trigger backoff. Call
   *  `handle.stop()` from inside `poll` once a terminal condition is
   *  reached (there is nothing generic this module can know about what
   *  "terminal" means for a given caller).
   */
  poll: () => Promise<void>;
}

export interface BackoffPollerHandle {
  /** Permanently ends this poller — no further polls, ever. Idempotent. */
  stop: () => void;
  /** Suspends polling: clears any pending timer and, if a poll happened to
   *  be in flight when this was called, prevents that poll's own
   *  completion from scheduling another one. No network activity occurs
   *  while paused. Idempotent. */
  pause: () => void;
  /** Resumes a paused poller with an immediate poll (a "catch up now"
   *  fetch) rather than waiting out whatever delay was pending before the
   *  pause — the whole point of pausing on a hidden tab is to skip wasted
   *  polls, not to skip the first real one once it's visible again.
   *  No-ops if not currently paused, or if already stopped. */
  resume: () => void;
  /** Polls right away instead of waiting out the pending delay — used when a
   *  live event says this thing just changed — but never sooner than
   *  `minGapMs` after the previous poll started: inside that gap it polls once
   *  at the gap's end instead (a burst of events is one poll, and the last
   *  change is never missed). If a poll is in flight, the next one follows it
   *  under the same rule. No-ops while paused or once stopped. */
  pollNow: (minGapMs?: number) => void;
  /** Changes the healthy cadence (a backup pace once live events are known to
   *  arrive, the normal pace again when they stop). A faster pace also brings
   *  an already-scheduled poll forward; a slower one applies from the next
   *  schedule. Backoff after failures still applies. */
  setIntervalMs: (intervalMs: number) => void;
}

/** Computes the delay after N consecutive failures (0 = about to make the
 *  first/next attempt after a success, i.e. the normal cadence). Doubles
 *  per failure, capped at maxIntervalMs: intervalMs, 2x, 4x, 8x, ... cap. */
export function computeBackoffDelayMs(consecutiveFailures: number, intervalMs: number, maxIntervalMs: number): number {
  if (consecutiveFailures <= 0) return intervalMs;
  return Math.min(intervalMs * 2 ** consecutiveFailures, maxIntervalMs);
}

export function startBackoffPolling(options: BackoffPollerOptions): BackoffPollerHandle {
  const { maxIntervalMs } = options;
  let intervalMs = options.intervalMs;
  let stopped = false;
  let paused = false;
  let busy = false;
  // A poll asked for while one was in flight: the gap to keep after it, or null.
  let rerunGapMs: number | null = null;
  let lastStartedAt = Number.NEGATIVE_INFINITY;
  let dueAt = 0;
  let scheduledAt = 0;
  let consecutiveFailures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const scheduleNext = (delay: number) => {
    if (stopped || paused) return;
    scheduledAt = Date.now();
    dueAt = scheduledAt + delay;
    timer = setTimeout(runOnce, delay);
  };

  const gapLeft = (minGapMs: number) => Math.max(0, lastStartedAt + minGapMs - Date.now());

  const runOnce = async () => {
    timer = undefined;
    if (stopped || paused) return;
    busy = true;
    lastStartedAt = Date.now();
    try {
      await options.poll();
      consecutiveFailures = 0;
    } catch {
      consecutiveFailures += 1;
    } finally {
      busy = false;
    }
    if (stopped || paused) return;
    if (rerunGapMs !== null) {
      const gap = rerunGapMs;
      rerunGapMs = null;
      scheduleNext(Math.min(gapLeft(gap), computeBackoffDelayMs(consecutiveFailures, intervalMs, maxIntervalMs)));
      return;
    }
    scheduleNext(computeBackoffDelayMs(consecutiveFailures, intervalMs, maxIntervalMs));
  };

  scheduleNext(intervalMs);

  return {
    stop: () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
    pause: () => {
      if (stopped || paused) return;
      paused = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
    resume: () => {
      if (stopped || !paused) return;
      paused = false;
      // If a poll from before the pause is still in flight, let it finish
      // naturally — its own completion will schedule the next one at the
      // normal cadence now that paused is false again. Kicking off a
      // second concurrent poll on top of it isn't worth the complexity for
      // what's a rare, brief overlap window.
      if (!busy) runOnce();
    },
    pollNow: (minGapMs = 0) => {
      if (stopped || paused) return;
      if (busy) {
        rerunGapMs = rerunGapMs === null ? minGapMs : Math.min(rerunGapMs, minGapMs);
        return;
      }
      const wait = gapLeft(minGapMs);
      // Already due by then: the pending poll covers it.
      if (timer !== undefined && dueAt <= Date.now() + wait) return;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      if (wait === 0) runOnce();
      else scheduleNext(wait);
    },
    setIntervalMs: (next: number) => {
      intervalMs = next;
      // Sooner than the poll already waiting (and not backing off a failure): bring it forward.
      if (timer === undefined || consecutiveFailures > 0 || scheduledAt + next >= dueAt) return;
      clearTimeout(timer);
      timer = undefined;
      scheduleNext(Math.max(0, scheduledAt + next - Date.now()));
    },
  };
}
